// src/scenes/Dashboard/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Avatar, Chip,
  CircularProgress, Alert, IconButton, Tooltip, Select,
  MenuItem, FormControl, LinearProgress, Divider, Paper,
  Table, TableBody, TableCell, TableRow, Badge
} from '@mui/material';
import {
  TrendingUp, ShoppingCart, People, Inventory,
  PointOfSale, Warning, Refresh, Store, Group,
  AccountBalance, LocalShipping, EmojiEvents,
  FiberManualRecord, AttachMoney, CreditCard,
  PersonAdd, BarChart
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart as RechartBar,
  Bar
} from 'recharts';
import api from '../../services/auth';

// ─── Utilitaires ────────────────────────────────────────────────────────────

const fmt = (n) =>
  n == null ? '—' :
  new Intl.NumberFormat('fr-HT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n) + ' HTG';

const fmtNum = (n) =>
  n == null ? '—' : new Intl.NumberFormat('fr-HT').format(n);

const fmtDate = (d) =>
  new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' }).format(new Date(d));

const COLORS_PIE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

// ─── Composants UI ──────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, sub, color = '#3b82f6', alert = false }) {
  return (
    <Card sx={{
      height: '100%',
      border: alert ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.08)',
      bgcolor: alert ? 'rgba(239,68,68,0.07)' : 'background.paper',
      transition: 'transform 0.2s',
      '&:hover': { transform: 'translateY(-2px)' }
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color: alert ? 'error.main' : 'text.primary' }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                {sub}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}22`, width: 42, height: 42 }}>
            {React.cloneElement(icon, { sx: { color, fontSize: 22 } })}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ icon, title, badge, badgeColor = 'primary' }) {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      <Avatar sx={{ bgcolor: 'action.hover', width: 32, height: 32 }}>
        {React.cloneElement(icon, { sx: { fontSize: 18, color: 'text.secondary' } })}
      </Avatar>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      {badge != null && (
        <Chip label={badge} size="small" color={badgeColor} sx={{ height: 20, fontSize: 11 }} />
      )}
    </Box>
  );
}

// ─── Sections ───────────────────────────────────────────────────────────────

function SectionVentes({ data }) {
  if (!data) return null;
  const pieData = (data.par_methode_paiement || []).map(m => ({
    name: m.methode_paiement,
    value: parseFloat(m.total)
  }));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <SectionTitle icon={<ShoppingCart />} title="Ventes du jour"
          badge={data.nombre_ventes_annulees > 0 ? `${data.nombre_ventes_annulees} annulées` : null}
          badgeColor="error" />
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center" p={1} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={800} color="success.main">
                {fmt(data.chiffre_affaires)}
              </Typography>
              <Typography variant="caption" color="text.secondary">CA</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center" p={1} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={800}>{fmtNum(data.nombre_ventes)}</Typography>
              <Typography variant="caption" color="text.secondary">Ventes</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center" p={1} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={800}>{fmt(data.panier_moyen)}</Typography>
              <Typography variant="caption" color="text.secondary">Panier moy.</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center" p={1} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={800} color="warning.main">{fmt(data.total_credit)}</Typography>
              <Typography variant="caption" color="text.secondary">Crédits</Typography>
            </Box>
          </Grid>
        </Grid>

        {pieData.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              Méthodes de paiement
            </Typography>
            <Box sx={{ height: 180, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />)}
                  </Pie>
                  <RechartsTooltip formatter={(v) => fmt(v)} />
                  <Legend iconSize={10} formatter={(v) => <span style={{ fontSize: 11, textTransform: 'capitalize' }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SectionTendances({ data }) {
  if (!data?.ca_par_jour) return null;

  // Remplir les jours manquants sur 7 jours
  const today = new Date();
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const found = data.ca_par_jour.find(x => x.date === dateStr);
    days.push({ date: dateStr, total: found?.total || 0, nombre_ventes: found?.nombre_ventes || 0 });
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <SectionTitle icon={<BarChart />} title="Tendances 7 jours" />
        <Box sx={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={days} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d)} tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
              <RechartsTooltip
                formatter={(v, name) => name === 'CA' ? fmt(v) : fmtNum(v)}
                labelFormatter={(d) => fmtDate(d)}
              />
              <Line yAxisId="left" type="monotone" dataKey="total" name="CA" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="nombre_ventes" name="Ventes" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}

function SectionStock({ data }) {
  if (!data) return null;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <SectionTitle icon={<Inventory />} title="État du stock"
          badge={data.produits_en_rupture?.count > 0 ? `${data.produits_en_rupture.count} rupture` : null}
          badgeColor="error" />

        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6}>
            <Box p={1.5} sx={{ bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700}>{fmtNum(data.total_produits_actifs)}</Typography>
              <Typography variant="caption" color="text.secondary">Produits actifs</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box p={1.5} sx={{ bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} color="error.main">{data.produits_en_rupture?.count || 0}</Typography>
              <Typography variant="caption" color="text.secondary">En rupture</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box p={1.5} sx={{ bgcolor: 'rgba(245,158,11,0.08)', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} color="warning.main">{data.produits_stock_faible?.count || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Stock faible</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box p={1.5} sx={{ bgcolor: 'rgba(16,185,129,0.08)', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" fontWeight={700} color="success.main" sx={{ display: 'block', mt: 0.5, fontSize: 12 }}>
                {fmt(data.valeur_stock_total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Valeur stock</Typography>
            </Box>
          </Grid>
        </Grid>

        {data.produits_en_rupture?.liste?.length > 0 && (
          <>
            <Typography variant="caption" color="error" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              Ruptures
            </Typography>
            {data.produits_en_rupture.liste.slice(0, 3).map((p, i) => (
              <Box key={i} display="flex" justifyContent="space-between" alignItems="center" py={0.5}
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2">{p.produit__nom}</Typography>
                <Chip label={p.succursale__nom} size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
              </Box>
            ))}
          </>
        )}

        {data.produits_stock_faible?.liste?.length > 0 && (
          <Box mt={1.5}>
            <Typography variant="caption" color="warning.main" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              Stock faible
            </Typography>
            {data.produits_stock_faible.liste.slice(0, 3).map((p, i) => (
              <Box key={i} py={0.5} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">{p.produit__nom}</Typography>
                  <Typography variant="caption" color="warning.main">{p.stock_actuel}/{p.stock_minimum}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (parseFloat(p.stock_actuel) / parseFloat(p.stock_minimum)) * 100)}
                  color="warning" sx={{ height: 3, borderRadius: 2, mt: 0.5 }}
                />
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function SectionClients({ data }) {
  if (!data) return null;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <SectionTitle icon={<People />} title="Clients & Crédits" />

        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6}>
            <Box p={1.5} sx={{ bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700}>{fmtNum(data.total_clients)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {data.nouveaux_aujourd_hui > 0 ? `+${data.nouveaux_aujourd_hui} aujourd'hui` : 'Clients'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box p={1.5} sx={{ bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2, textAlign: 'center' }}>
              <Typography variant="caption" fontWeight={700} color="error.main" sx={{ display: 'block', fontSize: 12 }}>
                {fmt(data.total_credit_en_cours)}
              </Typography>
              <Typography variant="caption" color="text.secondary">{data.nombre_clients_avec_credit} clients</Typography>
            </Box>
          </Grid>
        </Grid>

        {data.top_debiteurs?.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              Top débiteurs
            </Typography>
            {data.top_debiteurs.map((c, i) => (
              <Box key={i} py={0.75} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={600}>{c.prenom} {c.nom}</Typography>
                  <Typography variant="body2" color="error.main" fontWeight={700}>{fmt(c.solde_credit)}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (c.solde_credit / c.limite_credit) * 100)}
                  color="error" sx={{ height: 3, borderRadius: 2, mt: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Limite: {fmt(c.limite_credit)}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SectionSessions({ data }) {
  if (!data) return null;
  const ouvertes = data.sessions_ouvertes;
  const ecart = data.ecart_total_aujourd_hui;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Avatar sx={{ bgcolor: 'action.hover', width: 32, height: 32 }}>
            <PointOfSale sx={{ fontSize: 18, color: 'text.secondary' }} />
          </Avatar>
          <Typography variant="subtitle1" fontWeight={700}>Caisses</Typography>
          {ouvertes?.count > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <FiberManualRecord sx={{ fontSize: 10, color: 'success.main',
                animation: 'pulse 1.5s infinite', '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 }
                }
              }} />
              <Chip label={`${ouvertes.count} ouvertes`} size="small" color="success" sx={{ height: 20, fontSize: 11 }} />
            </Box>
          )}
        </Box>

        {ouvertes?.liste?.map((s, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" fontWeight={700}>{s.caissier_nom}</Typography>
                <Typography variant="caption" color="text.secondary">{s.succursale_nom} · {s.duree}</Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="body2" fontWeight={700} color="success.main">{fmt(s.chiffre_affaires)}</Typography>
                <Typography variant="caption" color="text.secondary">{s.nombre_ventes} ventes</Typography>
              </Box>
            </Box>
          </Paper>
        ))}

        {ouvertes?.count === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            Aucune caisse ouverte
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">Sessions fermées aujourd'hui</Typography>
          <Typography variant="caption" fontWeight={700}>{data.sessions_fermees_aujourd_hui}</Typography>
        </Box>
        {ecart !== 0 && ecart != null && (
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="text.secondary">Écart total</Typography>
            <Typography variant="caption" fontWeight={700} color={ecart < 0 ? 'error.main' : 'success.main'}>
              {ecart > 0 ? '+' : ''}{fmt(ecart)}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function SectionEquipe({ data }) {
  if (!data) return null;
  const medals = ['🥇', '🥈', '🥉'];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <SectionTitle icon={<Group />} title="Équipe"
          badge={`${data.actifs_aujourd_hui} actifs`} badgeColor="info" />

        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {Object.entries(data.par_role || {}).map(([role, count]) => (
            <Chip key={role} label={`${count} ${role}s`} size="small" variant="outlined" sx={{ height: 22, fontSize: 11, textTransform: 'capitalize' }} />
          ))}
        </Box>

        {data.top_vendeurs?.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
              Top vendeurs du jour
            </Typography>
            {data.top_vendeurs.map((v, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1.5} py={0.75}
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography fontSize={18}>{medals[i] || `${i + 1}.`}</Typography>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600}>
                    {v.caissier__first_name} {v.caissier__last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{v.nombre_ventes} ventes</Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} color="success.main">
                  {fmt(v.chiffre_affaires)}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SectionAchats({ data }) {
  if (!data) return null;
  const maxMontant = Math.max(...(data.top_fournisseurs || []).map(f => f.montant_total), 1);

  return (
    <Card>
      <CardContent>
        <SectionTitle icon={<LocalShipping />} title="Achats ce mois" />
        <Box display="flex" gap={3} mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="primary.main">{fmt(data.total_achats_mois)}</Typography>
            <Typography variant="caption" color="text.secondary">{data.nombre_achats_mois} commandes</Typography>
          </Box>
        </Box>

        {data.top_fournisseurs?.map((f, i) => (
          <Box key={i} mb={1.5}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2">{f.fournisseur__nom}</Typography>
              <Typography variant="body2" fontWeight={600}>{fmt(f.montant_total)}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(f.montant_total / maxMontant) * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── Page principale ─────────────────────────────────────────────────────────

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [succursales, setSuccursales] = useState([]);
  const [selectedSuccursale, setSelectedSuccursale] = useState('');
  const [isProprietaire, setIsProprietaire] = useState(false);
  const [userName, setUserName] = useState('');

  const fetchDashboard = useCallback(async (succursaleId = '') => {
    setLoading(true);
    setError(null);
    try {
      const params = succursaleId ? { succursale: succursaleId } : {};
      const res = await api.get('depot/dashboard/', { params });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Accès réservé aux managers et propriétaires.");
      } else {
        setError(err.response?.data?.error || 'Erreur lors du chargement du dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Récupérer les infos user depuis localStorage ou context
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setUserName(user.first_name || user.username || '');
      const role = user.role || '';
      setIsProprietaire(role === 'proprietaire' || role === 'super_admin');
    } catch (_) {}

    fetchDashboard();

    // Charger les succursales pour le filtre proprio
    api.get('succursales/').then(res => {
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setSuccursales(list);
    }).catch(() => {});
  }, [fetchDashboard]);

  const handleSuccursaleChange = (e) => {
    const val = e.target.value;
    setSelectedSuccursale(val);
    fetchDashboard(val);
  };

  // KPI résumés depuis les sections
  const kpis = data ? [
    {
      icon: <TrendingUp />, label: "CA Aujourd'hui",
      value: fmt(data.aujourd_hui?.chiffre_affaires), color: '#10b981'
    },
    {
      icon: <ShoppingCart />, label: "Ventes",
      value: fmtNum(data.aujourd_hui?.nombre_ventes),
      sub: data.aujourd_hui?.nombre_ventes_annulees > 0 ? `${data.aujourd_hui.nombre_ventes_annulees} annulées` : undefined,
      color: '#3b82f6'
    },
    {
      icon: <AttachMoney />, label: "Panier moyen",
      value: fmt(data.aujourd_hui?.panier_moyen), color: '#f59e0b'
    },
    {
      icon: <CreditCard />, label: "Crédits accordés",
      value: fmt(data.aujourd_hui?.total_credit),
      alert: (data.aujourd_hui?.total_credit || 0) > 0, color: '#ef4444'
    },
    {
      icon: <Inventory />, label: "Produits actifs",
      value: fmtNum(data.stock?.total_produits_actifs), color: '#6366f1'
    },
    {
      icon: <Warning />, label: "Ruptures",
      value: fmtNum(data.stock?.produits_en_rupture?.count),
      alert: (data.stock?.produits_en_rupture?.count || 0) > 0, color: '#ef4444'
    },
    {
      icon: <People />, label: "Clients",
      value: fmtNum(data.clients?.total_clients),
      sub: data.clients?.nouveaux_aujourd_hui > 0 ? `+${data.clients.nouveaux_aujourd_hui} aujourd'hui` : undefined,
      color: '#8b5cf6'
    },
    {
      icon: <PointOfSale />, label: "Caisses ouvertes",
      value: fmtNum(data.sessions?.sessions_ouvertes?.count), color: '#10b981'
    },
  ] : [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, mx: 'auto' }}>

      {/* ── En-tête ── */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight={800}>
            {userName ? `Bonjour, ${userName} 👋` : 'Dashboard'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date())}
          </Typography>
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          {isProprietaire && succursales.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedSuccursale}
                onChange={handleSuccursaleChange}
                displayEmpty
                sx={{ fontSize: 14 }}
              >
                <MenuItem value="">Toutes les succursales</MenuItem>
                {succursales.filter(s => s.is_active).map(s => (
                  <MenuItem key={s.id} value={s.id.toString()}>{s.nom}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Rafraîchir">
            <IconButton onClick={() => fetchDashboard(selectedSuccursale)} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading && !data ? (
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress size={48} />
        </Box>
      ) : data && (
        <>
          {/* ── KPI Cards ── */}
          <Grid container spacing={2} mb={3}>
            {kpis.map((kpi, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <KpiCard {...kpi} />
              </Grid>
            ))}
          </Grid>

          {/* ── Ventes + Tendances ── */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={5}>
              <SectionVentes data={data.aujourd_hui} />
            </Grid>
            <Grid item xs={12} md={7}>
              <SectionTendances data={data.tendances} />
            </Grid>
          </Grid>

          {/* ── Stock + Clients ── */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <SectionStock data={data.stock} />
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionClients data={data.clients} />
            </Grid>
          </Grid>

          {/* ── Sessions + Équipe ── */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <SectionSessions data={data.sessions} />
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionEquipe data={data.equipe} />
            </Grid>
          </Grid>

          {/* ── Achats ── */}
          <SectionAchats data={data.achats} />
        </>
      )}
    </Box>
  );
}
